/* eslint-disable react/no-array-index-key */
import { ICase } from '@brands/services/cases/types/ICase';
import { ImageWithNotes } from '@brands/services/forms/types/ImageWithNotes';
import { getUserRoleName } from '@brands/Utils/getFriendlyName';
import {
  formatOHCPhoneNumber,
  getOHCInsuranceCarrier,
  getOHCMedicalConditionsText,
  getOHCPharmacyLines,
  getVisibleOHCGoals,
  getVisibleOHCImages,
  getVisibleOHCNotes,
  hasOHCReportHtml,
  hasOHCText,
  sanitizeOHCReportHtml,
} from '@brands/Dashboard/ConsultationPage/utils/oralHealthCoachingReport';
import { Document, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { parseDocument } from 'htmlparser2';
import moment from 'moment-timezone';
import React from 'react';

const PRIMARY = '#2E73BD';
const TEXT_DARK = '#111827';
const TEXT_MUTED = '#627182';
const BORDER = '#DCE2E8';

// Care-plan summaries, goals and notes are free text that can run longer than a page, so their blocks
// must be allowed to break. An unbreakable (`wrap={false}`) block taller than the page makes react-pdf
// stack every line on top of each other. Instead, each block's label asks for this much room after it
// (about two lines) so it moves to the next page with its text rather than being stranded at the bottom.
const LABEL_MIN_PRESENCE_AHEAD = 40;

const styles = StyleSheet.create({
  page: {
    paddingTop: 58,
    paddingHorizontal: 58,
    paddingBottom: 34,
    fontSize: 11,
    fontFamily: 'Helvetica',
    color: TEXT_DARK,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 42,
    width: '100%',
  },
  logoWrap: {
    flexDirection: 'column',
  },
  logoImage: {
    width: 136,
    height: 34,
    objectFit: 'contain',
  },
  poweredByText: {
    fontSize: 8,
    marginTop: 4,
    color: TEXT_MUTED,
  },
  headerRight: {
    alignItems: 'flex-start',
    width: 190,
  },
  reportTitle: {
    fontSize: 12,
    color: TEXT_MUTED,
    lineHeight: 1.3,
  },
  date: {
    fontSize: 12,
    color: TEXT_MUTED,
    lineHeight: 1.3,
  },
  section: {
    marginBottom: 28,
    width: '100%',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    marginBottom: 18,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  column: {
    width: '45%',
  },
  field: {
    marginBottom: 16,
  },
  label: {
    fontSize: 12,
    color: TEXT_MUTED,
    lineHeight: 1.25,
  },
  value: {
    fontSize: 12,
    color: TEXT_DARK,
    lineHeight: 1.35,
  },
  richText: {
    marginTop: 2,
  },
  paragraph: {
    fontSize: 12,
    color: TEXT_DARK,
    lineHeight: 1.35,
    marginBottom: 8,
  },
  listItem: {
    fontSize: 12,
    color: TEXT_DARK,
    lineHeight: 1.35,
    marginBottom: 2,
  },
  link: {
    fontSize: 12,
    color: PRIMARY,
    lineHeight: 1.35,
    textDecoration: 'none',
  },
  block: {
    marginBottom: 18,
  },
  goalBlock: {
    marginBottom: 16,
  },
  educationBlock: {
    marginTop: 8,
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    width: '100%',
  },
  imageItem: {
    width: '48%',
    marginBottom: 22,
  },
  imageFrame: {
    borderWidth: 1,
    borderColor: '#D5E0EA',
    height: 150,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  imageCaption: {
    fontSize: 12,
    lineHeight: 1.35,
    color: TEXT_DARK,
    marginTop: 8,
  },
  noteItem: {
    marginBottom: 18,
  },
  noteMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  noteMetaText: {
    color: TEXT_MUTED,
    fontSize: 10,
    lineHeight: 1.35,
    marginRight: 6,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: TEXT_MUTED,
  },
});

interface Props {
  thisCase: ICase;
  logoBase64: string;
  images?: ImageWithNotes[];
}

const OHCPdfDocument: React.FC<Props> = ({ thisCase, logoBase64, images = [] }) => {
  const timezone = moment.tz.guess();

  const formatDateWithTime = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    const date = moment.utc(dateStr).tz(timezone);
    return date.isValid() ? date.format('MMMM D, YYYY hh:mma') : '';
  };

  const formatDateOnly = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    const date = moment.utc(dateStr).tz(timezone);
    return date.isValid() ? date.format('MMMM D, YYYY') : '';
  };

  const formatDateOfBirth = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    const date = moment(dateStr);
    return date.isValid() ? date.format('MM/DD/YYYY') : dateStr;
  };

  const formatNoteDate = (dateStr?: string | null): string => {
    if (!dateStr) return '';
    const date = moment.utc(dateStr).tz(timezone);
    return date.isValid() ? date.format('MM/DD/YYYY [at] hh:mma') : '';
  };

  const renderHtmlToPdf = (html: string): React.ReactElement => {
    const dom = parseDocument(sanitizeOHCReportHtml(html));
    let orderedIndex = 1;

    const renderNodes = (nodes: any[], parentType?: string): any[] =>
      nodes.flatMap((node, index) => {
        if (node.type === 'text') return <Text key={index} style={styles.value}>{node.data}</Text>;
        if (node.name === 'strong' || node.name === 'b')
          return (
            <Text key={index} style={{ fontWeight: 'bold' }}>
              {renderNodes(node.children)}
            </Text>
          );
        if (node.name === 'em' || node.name === 'i')
          return (
            <Text key={index} style={{ fontStyle: 'italic' }}>
              {renderNodes(node.children)}
            </Text>
          );
        if (node.name === 'u')
          return (
            <Text key={index} style={{ textDecoration: 'underline' }}>
              {renderNodes(node.children)}
            </Text>
          );
        if (node.name === 'br') return <Text key={index}>{'\n'}</Text>;
        if (node.name === 'p')
          return (
            <Text key={index} style={styles.paragraph}>
              {renderNodes(node.children)}
            </Text>
          );
        if (node.name === 'ul') {
          orderedIndex = 1;
          return <View key={index}>{renderNodes(node.children, 'ul')}</View>;
        }
        if (node.name === 'ol') {
          orderedIndex = 1;
          return <View key={index}>{renderNodes(node.children, 'ol')}</View>;
        }
        if (node.name === 'li') {
          const prefix = parentType === 'ol' ? `${orderedIndex++}. ` : '- ';
          return (
            <Text key={index} style={styles.listItem}>
              {prefix}
              {renderNodes(node.children)}
            </Text>
          );
        }
        if (node.name === 'a') {
          const href = node.attribs?.href || '';
          return (
            <Link key={index} src={href} style={styles.link}>
              {renderNodes(node.children)}
            </Link>
          );
        }
        return renderNodes(node.children || []);
      });

    return <View style={styles.richText}>{renderNodes(dom.children)}</View>;
  };

  const Header: React.FC = () => (
    <View style={styles.header} fixed>
      <View style={styles.logoWrap}>
        <Image src={logoBase64} style={styles.logoImage} />
        {thisCase?.organization?.uses_custom_logo && <Text style={styles.poweredByText}>Powered by Dentistry.One</Text>}
      </View>
      <View style={styles.headerRight}>
        <Text style={styles.reportTitle}>Oral Health Coaching Report</Text>
        <Text style={styles.date}>{formatDateOnly(thisCase.completed_at)}</Text>
      </View>
    </View>
  );

  const Footer: React.FC = () => (
    <Text style={styles.footer} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} fixed />
  );

  const ReportPage = ({ children }: { children: React.ReactNode }): JSX.Element => (
    <Page size="LETTER" style={styles.page}>
      <Header />
      {children}
      <Footer />
    </Page>
  );

  const renderSection = (title: string, children: React.ReactNode, wrap = true): JSX.Element => (
    <View style={styles.section} wrap={wrap}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <View style={styles.divider} />
      {children}
    </View>
  );

  const renderField = (label: string, value?: string | React.ReactNode): JSX.Element | null => {
    if (typeof value === 'string' && !hasOHCText(value)) return null;
    if (value === undefined || value === null) return null;

    return (
      <View style={styles.field} wrap={false}>
        <Text style={styles.label}>{label}</Text>
        {typeof value === 'string' ? <Text style={styles.value}>{value}</Text> : value}
      </View>
    );
  };

  const medSnap = thisCase.medical_history_snapshot;
  const condSnap = thisCase.medical_conditions_snapshot;
  const insurance = getOHCInsuranceCarrier(thisCase);
  const conditions = getOHCMedicalConditionsText(condSnap);
  const pharmacyLines = getOHCPharmacyLines(medSnap);
  const carePlan = thisCase.oral_health_coaching_report?.care_plan;
  const consultationSummary = carePlan?.consultation_summary || '';
  const hasConsultationSummary = hasOHCReportHtml(consultationSummary);
  const visibleGoals = getVisibleOHCGoals(carePlan?.goals);
  const visibleImages = getVisibleOHCImages(images);
  const visibleNotes = getVisibleOHCNotes(thisCase.one_report?.notes);
  const hasCarePlan = hasConsultationSummary || visibleGoals.length > 0;

  return (
    <Document>
      <ReportPage>
        {renderSection(
          'Care team',
          <View style={styles.row}>
            <View style={styles.column}>
              {renderField(
                'Care Advisor',
                `${thisCase.care_advisor.full_name}${thisCase.care_advisor.status === 'Deactivated' ? ' (Deactivated)' : ''}`,
              )}
            </View>
            <View style={styles.column}>
              {renderField(
                'Collector',
                thisCase.collector
                  ? `${thisCase.collector.full_name}${thisCase.collector.status === 'Deactivated' ? ' (Deactivated)' : ''}`
                  : undefined,
              )}
            </View>
          </View>,
          false,
        )}

        {renderSection(
          'Appointment details',
          <View style={styles.row}>
            <View style={styles.column}>
              {renderField('Video consultation', formatDateWithTime(thisCase.video_conference_date || thisCase.created_at))}
              {renderField('Service requested', formatDateWithTime(thisCase.created_at))}
            </View>
            <View style={styles.column}>
              {renderField('Consult type', 'Oral Health Coaching')}
              {renderField('Report completed', formatDateWithTime(thisCase.completed_at))}
            </View>
          </View>,
          false,
        )}

        {renderSection(
          'Patient details',
          <View style={styles.row}>
            <View style={styles.column}>
              {renderField(
                'Patient name',
                `${thisCase.patient.full_name}${thisCase.patient.status === 'Deactivated' ? ' (Deactivated)' : ''}`,
              )}
              {renderField('DOB', formatDateOfBirth(thisCase.patient.basic_info.date_of_birth))}
              {renderField('Insurance', insurance)}
              {renderField('Email', thisCase.patient.email)}
              {renderField('Phone number', formatOHCPhoneNumber(thisCase.patient.phone_number))}
            </View>
            <View style={styles.column}>
              {renderField('Conditions', conditions)}
              {renderField('Medications', medSnap?.medications)}
              {renderField('Allergies', medSnap?.medications_allergies)}
              {pharmacyLines.length > 0 &&
                renderField(
                  'Pharmacy',
                  <View>
                    {pharmacyLines.map((line) => (
                      <Text style={styles.value} key={line}>
                        {line}
                      </Text>
                    ))}
                  </View>,
                )}
            </View>
          </View>,
          false,
        )}
      </ReportPage>

      {hasCarePlan && (
        <ReportPage>
          {renderSection(
            'Your care plan',
            <>
              {hasConsultationSummary && (
                <View style={styles.block}>
                  <Text style={styles.label} minPresenceAhead={LABEL_MIN_PRESENCE_AHEAD}>
                    Summary
                  </Text>
                  {renderHtmlToPdf(consultationSummary)}
                </View>
              )}

              {visibleGoals.map((goal, index) => (
                <View key={goal.id || index} style={styles.goalBlock}>
                  <Text style={styles.label} minPresenceAhead={LABEL_MIN_PRESENCE_AHEAD}>{`Goal ${index + 1}`}</Text>
                  {renderHtmlToPdf(goal.goal_text || '')}

                  {goal.educations.map((education) => (
                    <View key={education.id || `${education.title}-${education.url}`} style={styles.educationBlock}>
                      {hasOHCText(education.title) && <Text style={styles.label}>{education.title}</Text>}
                      {hasOHCText(education.url) && (
                        <Link src={education.url} style={styles.link}>
                          {education.url}
                        </Link>
                      )}
                    </View>
                  ))}
                </View>
              ))}
            </>,
          )}
        </ReportPage>
      )}

      {visibleImages.length > 0 && (
        <ReportPage>
          {renderSection(
            'Images',
            <View style={styles.imageGrid}>
              {visibleImages.map((img, index) => (
                <View key={img.id || img.src || index} style={styles.imageItem} wrap={false}>
                  <View style={styles.imageFrame}>
                    <Image src={img.src} style={styles.image} />
                  </View>
                  {hasOHCText(img.notes) && <Text style={styles.imageCaption}>{img.notes}</Text>}
                </View>
              ))}
            </View>,
          )}
        </ReportPage>
      )}

      {visibleNotes.length > 0 && (
        <ReportPage>
          {renderSection(
            'Notes',
            <View>
              {visibleNotes.map((note) => (
                <View key={note.id} style={styles.noteItem}>
                  <View style={styles.noteMeta} minPresenceAhead={LABEL_MIN_PRESENCE_AHEAD}>
                    <Text style={styles.noteMetaText}>{formatNoteDate(note.last_edited_at || note.created_at)}</Text>
                    <Text style={styles.noteMetaText}>|</Text>
                    <Text style={styles.noteMetaText}>
                      {note.created_by.full_name} ({getUserRoleName(note.created_by.role.name)})
                    </Text>
                  </View>
                  {renderHtmlToPdf(note.note)}
                </View>
              ))}
            </View>,
          )}
        </ReportPage>
      )}
    </Document>
  );
};

export default OHCPdfDocument;
